import type { BlogCollectionView, UserProfile } from "@lexicon/models";

import { useIsLargeScreen } from "@alextheman/components";
import { InternalLink } from "@alextheman/components/routing";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";

import { useAuth } from "src/AuthContextProvider";
import createPaginationGroup from "src/groups/pagination";
import createListQueryBoundary from "src/groups/QueryBoundary/creators/createListQueryBoundary";
import usePagination from "src/hooks/usePagination";
import BlogCollectionsList from "src/resources/BlogCollections/components/BlogCollectionsList";
import BlogCollectionsTable from "src/resources/BlogCollections/components/BlogCollectionsTable";
import useBlogCollectionsQuery from "src/resources/BlogCollections/queries/useBlogCollectionsQuery";

interface UserBlogCollectionsProps {
  user: UserProfile;
}

function UserBlogCollections({ user }: UserBlogCollectionsProps) {
  const pagination = usePagination<BlogCollectionView>({
    pageNumber: 0,
    pageSize: 100,
    sortColumn: "createdAt",
    sortDirection: "desc",
  });
  const PaginationGroup = createPaginationGroup(pagination);
  const { data, isPending, error } = useBlogCollectionsQuery({
    ...pagination.state.paginationSettings,
    userId: user.id,
  });
  const QueryBoundary = createListQueryBoundary({
    query: { data: data?.blogCollections, isLoading: isPending, error },
  });
  const isLargeScreen = useIsLargeScreen();
  const { currentUser } = useAuth();

  return (
    <Stack spacing={2}>
      {currentUser?.id === user.id ? (
        <Button component={InternalLink} to="/blog-collections/new" variant="contained">
          <Stack direction="row" spacing={4}>
            + New Blog Collection
          </Stack>
        </Button>
      ) : null}
      {isLargeScreen ? (
        <BlogCollectionsTable
          PaginationGroup={PaginationGroup}
          QueryBoundary={QueryBoundary}
          totalRecordCount={data?.count}
        />
      ) : (
        <BlogCollectionsList
          PaginationGroup={PaginationGroup}
          QueryBoundary={QueryBoundary}
          totalRecordCount={data?.count}
        />
      )}
    </Stack>
  );
}

export default UserBlogCollections;
